import { useMemo, useState } from 'react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { useGetDocumentContent } from '../../api/useGetDocumentContent';

export type Props = {
  open: boolean;
  documentId: string | null;
  documentName: string;
  fileUrl?: string;
  onClose: () => void;
};

export function computeContentMetrics(content: string | null | undefined): {
  charCount: number;
  wordCount: number;
} {
  if (!content) {
    return { charCount: 0, wordCount: 0 };
  }
  const trimmed = content.trim();
  const words = trimmed.length > 0 ? trimmed.split(/\s+/).length : 0;
  return { charCount: content.length, wordCount: words };
}

export function useDocumentContentModal(props: Props) {
  const [copied, setCopied] = useState(false);

  const query = useGetDocumentContent(props.open ? props.documentId : null);

  const content = query.data?.content ?? null;
  const extractedAt = query.data?.extractedAt ?? null;

  const formattedExtractedAt = useMemo(() => {
    if (!extractedAt) return null;
    try {
      return format(new Date(extractedAt), "d MMMM yyyy 'à' HH:mm", { locale: fr });
    } catch {
      return null;
    }
  }, [extractedAt]);

  const { charCount, wordCount } = useMemo(
    () => computeContentMetrics(content),
    [content]
  );


  async function handleCopyContent() {
    if (!content) return;
    try {
      await navigator.clipboard.writeText(content);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch (err) {
      console.error('Failed to copy document content:', err);
    }
  }

  function handleOpenOriginal() {
    if (props.fileUrl) {
      window.open(props.fileUrl, '_blank', 'noopener,noreferrer');
    }
  }

  function handleClose() {
    setCopied(false);
    props.onClose();
  }

  return {
    isLoading: query.isLoading,
    isError: query.isError,
    content,
    extractedAt,
    formattedExtractedAt,
    charCount,
    wordCount,
    copied,
    handleCopyContent,
    handleOpenOriginal,
    handleClose,
  };
}
